/** A byte count for humans: "915 KB", "2.4 MB". One definition — every upload
 *  row and picker renders sizes through it. */
export const humanSize = (bytes: number): string => {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** A timestamp for a table cell: "Sep 22, 2026, 10:14 AM" in the viewer's locale. */
export const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });

/** A date without the time: "Sep 22, 2026". */
export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' });

/** A day in the current context, no year: "Sep 22". */
export const formatDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** A day and time in the current context, no year: "Oct 7, 8:12 AM". */
export const formatDayTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** How long ago, for a list: "Just now", "5m ago", "2h ago", "Yesterday", then "Oct 2". */
export const formatAgo = (iso: string, now: Date = new Date()): string => {
  const then = new Date(iso);
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${String(minutes)}m ago`;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (then.getTime() >= startOfToday) return `${String(Math.floor(minutes / 60))}h ago`;
  if (then.getTime() >= startOfToday - 86_400_000) return 'Yesterday';
  return formatDay(iso);
};

/** A calendar month: "August 2026". `month` is 1 to 12. */
export const formatMonth = (year: number, month: number): string =>
  new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

/** A wall-clock "HH:MM" as people say it: "7:00 AM". No date, so no timezone shift. */
export const formatClock = (clock: string): string => {
  const [hour = 0, minute = 0] = clock.split(':').map(Number);
  return new Date(Date.UTC(2000, 0, 1, hour, minute)).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

const usdCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const usdWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const wholeNumber = new Intl.NumberFormat('en-US');

/** Dollars and cents: "$1,412.08". */
export const formatUsd = (n: number): string => usdCents.format(n);

/** Whole dollars for headline figures: "$1,412". */
export const formatUsdWhole = (n: number): string => usdWhole.format(n);

/** A count with separators: "2,940". */
export const formatCount = (n: number): string => wholeNumber.format(n);

/** A large count, compact: "412.0M", "6.2K", "940". */
export const formatCompact = (n: number): string =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}K`
      : String(Math.round(n));

/** A 0 to 1 share as a whole percent: "86%". */
export const formatPercent = (share: number): string => `${String(Math.round(share * 100))}%`;

const dayKeyDate = (key: string): Date => {
  const [y = 1970, m = 1, d = 1] = key.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/** A "YYYY-MM-DD" day key, already in the tenant's zone, as "Oct 7" (no zone shift). */
export const formatDayKey = (key: string): string =>
  dayKeyDate(key).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** A "YYYY-MM-DD" day key as a compact axis label: "10/7". */
export const formatDayKeyShort = (key: string): string => {
  const d = dayKeyDate(key);
  return `${String(d.getUTCMonth() + 1)}/${String(d.getUTCDate())}`;
};

/** A "YYYY-MM-DDTHH:00" hour key as "2 PM". */
export const formatHourKey = (key: string): string => {
  const hour = Number(key.slice(11, 13));
  return new Date(Date.UTC(2000, 0, 1, hour)).toLocaleTimeString('en-US', {
    hour: 'numeric',
    timeZone: 'UTC',
  });
};

/** The month of a "YYYY-MM" key: "October". */
export const formatMonthName = (key: string): string =>
  dayKeyDate(`${key}-01`).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

/** When something last happened: "Today, 2:08 PM", else "Oct 2". */
export const formatLastActive = (iso: string, now: Date = new Date()): string => {
  const then = new Date(iso);
  const sameDay =
    then.getFullYear() === now.getFullYear() &&
    then.getMonth() === now.getMonth() &&
    then.getDate() === now.getDate();
  if (!sameDay) return formatDay(iso);
  return `Today, ${then.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

/** A per-million-token list rate: "$4", "$0.20". */
export const formatRate = (n: number): string =>
  Number.isInteger(n) ? `$${String(n)}` : usdCents.format(n);

/** Whole dollars, but cents under $10 so a small amount never reads "$0": "$2,694", "$0.38". */
export const formatUsdBrief = (n: number): string =>
  n < 10 ? usdCents.format(n) : usdWhole.format(n);

/** When something happened, for a row: "Today, 2:08 PM", else "Oct 2, 1:41 PM". */
export const formatWhen = (iso: string, now: Date = new Date()): string =>
  formatLastActive(iso, now).startsWith('Today') ? formatLastActive(iso, now) : formatDayTime(iso);
